import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Bookmark, BookmarkCheck, Check, Copy, ExternalLink, FileText, Quote } from "lucide-react";
import { isSafeHref } from "../shared/safeHref.js";
import { CitationDialog } from "./CitationDialog.js";
import { getWork, getWorkContent, getWorkRelations, setWorkFavorite, subscribeWorkFavorites, workDetailUrl, type WorkDetail, type WorkRelationPage } from "./workApi.js";

function authorName(author: WorkDetail["authors"][number]) {
  if (typeof author === "string") return author;
  return author.name || [author.givenName, author.familyName].filter(Boolean).join(" ") || "未知作者";
}

function issuedLabel(issued: WorkDetail["issued"]) {
  if (!issued) return "发表日期待补充";
  return [issued.year, issued.month, issued.day].filter((value) => value !== null && value !== undefined).join("-");
}

function orcidHref(value: string | null | undefined) {
  const normalized = String(value || "").replace(/^https?:\/\/orcid\.org\//i, "").toUpperCase();
  return /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(normalized) ? `https://orcid.org/${normalized}` : "";
}

function keywordLabel(value: unknown) {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  const keyword = value as Record<string, unknown>;
  return String(keyword.display_name || keyword.name || keyword.term || keyword.keyword || keyword.label || keyword.value || "").trim();
}

function abstractFingerprint(content: string) {
  return content.replace(/^\s*abstract\s*[:—-]?\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();
}

function metricLabel(metric: WorkDetail["metrics"][number]) {
  const labels: Record<string, string> = {
    impact_factor: "JIF",
    five_year_impact_factor: "5-year JIF",
    jcr_quartile: "JCR 分区",
    citescore: "CiteScore",
    citescore_quartile: "CiteScore 分区",
    citation_count: "被引次数",
    reference_count: "参考文献数"
  };
  return `${labels[metric.type] || metric.type}${metric.period ? ` ${metric.period}` : ""}：${metric.value}`;
}

function RelationList({ page, empty, onLoadMore }: { page: WorkRelationPage; empty: string; onLoadMore: () => void }) {
  if (!page.items.length && !page.unresolvedCount) return <p className="paper-empty">{empty}</p>;
  return (
    <div className="paper-relation-list">
      {page.unresolvedCount > 0 && (
        <div className="paper-relation-summary">
          <strong>{page.resolvedCount} 条已解析，{page.unresolvedCount} 条待补全</strong>
          <span>总数来自来源记录中的引文关系；没有题名的条目暂不重复列出。</span>
        </div>
      )}
      {page.items.map((item) => item.work ? (
        <a key={item.edgeId} href={workDetailUrl(item.work.id)} target="_blank" rel="noreferrer">
          <strong>{item.work.title}</strong>
          <span>{item.work.venue || "来源待补充"}{item.work.year ? ` · ${item.work.year}` : ""}</span>
        </a>
      ) : (
        <article key={item.edgeId}>
          <strong>{item.rawReferenceText}</strong>
          <span>书目信息待解析</span>
        </article>
      ))}
      {page.nextCursor && <button className="paper-relations-more" type="button" onClick={onLoadMore}>加载更多</button>}
    </div>
  );
}

const emptyRelationPage = (): WorkRelationPage => ({ items: [], nextCursor: null, total: 0, resolvedCount: 0, unresolvedCount: 0 });

export function PaperDetailPage({ workId, sessionId }: { workId: string; sessionId?: string | null }) {
  const [work, setWork] = useState<WorkDetail | null>(null);
  const [content, setContent] = useState<Awaited<ReturnType<typeof getWorkContent>> | null>(null);
  const [relations, setRelations] = useState({
    references: emptyRelationPage(),
    cited_by: emptyRelationPage()
  });
  const [relationTab, setRelationTab] = useState<"references" | "cited_by">("references");
  const [citationOpen, setCitationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedDoi, setCopiedDoi] = useState(false);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getWork(workId, sessionId).then(async (value) => {
      if (!active) return;
      setWork(value);
      const [loadedContent, references, citedBy] = await Promise.all([
        value.contentState === "partial_full_text" || value.contentState === "full_text" ? getWorkContent(workId) : Promise.resolve(null),
        getWorkRelations(workId, "references"),
        getWorkRelations(workId, "cited_by")
      ]);
      if (!active) return;
      setContent(loadedContent);
      setRelations({ references, cited_by: citedBy });
    }).catch((reason) => {
      if (active) setError(reason instanceof Error && reason.message === "not found" ? "这篇论文不存在，或当前对话无权访问该链接。" : "论文信息加载失败，请稍后重试。");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [workId, sessionId]);

  useEffect(() => subscribeWorkFavorites((event) => {
    if (event.workId === workId) setWork((current) => current ? { ...current, isSaved: event.isSaved } : current);
  }), [workId]);

  const originalLink = useMemo(() => work?.links.find((link) => link.type === "doi_landing" || link.type === "publisher_landing")?.url || work?.links[0]?.url, [work]);
  const doi = useMemo(() => work?.identifiers.find((identifier) => identifier.scheme === "doi")?.value_normalized, [work]);
  const pdfAsset = useMemo(() => work?.contentAssets.find((asset) => asset.media_type === "application/pdf" && asset.fileUrl), [work]);
  const venueMetrics = useMemo(() => {
    const priority = new Map([
      ["impact_factor", 0],
      ["jcr_quartile", 1],
      ["citescore", 2],
      ["citescore_quartile", 3]
    ]);
    return (work?.metrics || [])
      .filter((metric) => metric.targetType === "venue" && priority.has(metric.type))
      .sort((left, right) => (priority.get(left.type) ?? 99) - (priority.get(right.type) ?? 99))
      .slice(0, 4);
  }, [work]);
  const venueCover = useMemo(() => work?.venue?.mediaAssets.find((asset) => asset.media_role === "cover" && asset.image_url)?.image_url, [work]);
  const keywords = useMemo(() => Array.from(new Set((work?.keywords || []).map(keywordLabel).filter(Boolean))), [work]);
  const abstracts = useMemo(() => {
    const seen = new Set<string>();
    return (work?.abstracts || []).filter((abstract) => {
      const fingerprint = abstractFingerprint(abstract.content);
      if (!fingerprint || seen.has(fingerprint)) return false;
      seen.add(fingerprint);
      return true;
    });
  }, [work]);
  const hasTranslatedAbstract = abstracts.some((abstract) => abstract.kind === "translated");
  const authorsWithAffiliations = useMemo(() => (work?.authors || []).filter((author) => typeof author !== "string" && author.affiliations?.length), [work]);

  async function toggleFavorite() {
    if (!work || saving) return;
    setSaving(true);
    setActionError("");
    try {
      const next = !work.isSaved;
      await setWorkFavorite(work.id, next, next ? sessionId : undefined);
      setWork({ ...work, isSaved: next });
    } catch {
      setActionError(work.isSaved ? "取消收藏失败，请重试。" : "收藏失败，请重试。");
    } finally {
      setSaving(false);
    }
  }

  async function loadMoreRelations(direction: "references" | "cited_by") {
    const cursor = relations[direction].nextCursor;
    if (!cursor) return;
    setActionError("");
    try {
      const page = await getWorkRelations(workId, direction, cursor);
      setRelations((old) => ({ ...old, [direction]: { ...page, items: [...old[direction].items, ...page.items] } }));
    } catch {
      setActionError("相关文献加载失败，请重试。");
    }
  }

  async function copyDoi() {
    if (!doi) return;
    setActionError("");
    try {
      await navigator.clipboard.writeText(doi);
      setCopiedDoi(true);
    } catch {
      setActionError("DOI 复制失败，请手动选择复制。");
    }
  }

  function contentStateLabel(state: WorkDetail["contentState"]) {
    if (state === "full_text") return "全文";
    if (state === "partial_full_text") return "部分全文";
    if (state === "processing") return "全文解析中";
    if (state === "parse_failed") return "全文解析失败";
    if (state === "abstract_only") return "当前仅有摘要";
    return "暂未获取全文";
  }

  if (loading) return <main className="paper-page paper-page-state">正在加载论文…</main>;
  if (error || !work) return <main className="paper-page paper-page-state"><p>{error || "论文不存在。"}</p><button type="button" onClick={() => history.back()}>返回</button></main>;

  return (
    <main className="paper-page">
      <header className="paper-topbar">
        <button type="button" onClick={() => history.length > 1 ? history.back() : location.assign("/")}><ArrowLeft size={18} /> 返回</button>
        <span>论文详情</span>
        <i aria-hidden="true" />
      </header>
      <div className="paper-layout">
        <article className="paper-main">
          <section className="paper-hero">
            <div className="paper-kicker"><span>{issuedLabel(work.issued)}</span><span>{work.publication.venueTitle || work.venue?.title || "来源待补充"}</span>{doi && <button type="button" onClick={() => void copyDoi()} title={`DOI ${doi}`}>{copiedDoi ? <Check size={13} /> : <Copy size={13} />} DOI {copiedDoi ? "已复制" : doi}</button>}</div>
            <h1>{work.title}</h1>
            {work.subtitle && <p className="paper-subtitle">{work.subtitle}</p>}
            <div className="paper-authors">{work.authors.map((author, index) => {
              const detail = typeof author === "string" ? null : author;
              const href = orcidHref(detail?.orcid);
              return <span className="paper-author" data-initial={authorName(author).trim().slice(0, 1).toUpperCase()} key={`${authorName(author)}-${index}`}><strong>{authorName(author)}</strong>{href && <a href={href} title="打开作者的 ORCID 研究者身份页" target="_blank" rel="noreferrer">ORCID</a>}</span>;
            })}</div>
            {authorsWithAffiliations.length > 0 && <details className="paper-affiliations"><summary>作者与机构（{authorsWithAffiliations.length}）</summary><div>{authorsWithAffiliations.map((author, index) => typeof author === "string" ? null : <p key={`${authorName(author)}-${index}`}><strong>{authorName(author)}</strong><span>{Array.from(new Set(author.affiliations?.map((affiliation) => affiliation.name).filter(Boolean))).join("；")}</span></p>)}</div></details>}
            {work.recordStatus === "retracted" && <div className="paper-warning">撤稿提醒：该论文已撤稿，请在使用和引用前核实原因。</div>}
            <div className="paper-actions">
              <button type="button" aria-pressed={work.isSaved} onClick={() => void toggleFavorite()} disabled={saving}>
                {work.isSaved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />} {saving ? "处理中…" : work.isSaved ? "已收藏" : "收藏"}
              </button>
              <button type="button" onClick={() => setCitationOpen(true)}><Quote size={16} /> 引用</button>
              {pdfAsset?.fileUrl && <a href={pdfAsset.fileUrl} target="_blank" rel="noreferrer"><FileText size={16} /> 阅读 PDF</a>}
              {originalLink && isSafeHref(originalLink) && <a href={originalLink} target="_blank" rel="noreferrer"><ExternalLink size={16} /> 查看原文</a>}
            </div>
            {actionError && <p className="paper-action-error" role="alert">{actionError}</p>}
            {keywords.length > 0 && <div className="paper-keywords">{keywords.map((keyword) => <span key={keyword}>{keyword}</span>)}</div>}
          </section>

          <section className="paper-content-card" id="abstract">
            <h2>摘要</h2>
            {abstracts.length ? abstracts.map((abstract) => <div className="paper-abstract" key={abstract.id}>
              <h3>{abstract.kind === "translated" ? "中文翻译" : abstract.kind === "original" ? "原文摘要" : "摘要"}{abstract.language && abstract.language !== "und" ? ` · ${abstract.language}` : abstract.language === "und" ? " · 语言待识别" : ""}</h3>
              {Array.isArray(abstract.sections) && abstract.sections.length ? abstract.sections.map((section, index) => <section key={`${section.label || "section"}-${index}`}><strong>{section.label}</strong><p>{section.content}</p></section>) : <p>{abstract.content}</p>}
            </div>) : <p className="paper-empty">当前来源尚未提供可展示的摘要，系统会在后续元数据补全中继续尝试获取。</p>}
            {abstracts.length > 0 && !hasTranslatedAbstract && <p className="paper-translation-note">中文翻译尚未生成；上方内容均为来源提供的原文摘要。</p>}
          </section>

          <section className="paper-content-card" id="fulltext">
            <div className="paper-section-heading"><h2>正文与段落</h2><span>{contentStateLabel(work.contentState)}</span></div>
            {content?.sections.length ? content.sections.map((section) => <section className="paper-document-section" id={`section-${section.id}`} key={section.id}><h3>{section.heading || section.section_type}</h3><p>{section.content}</p></section>) : <div className="paper-content-placeholder"><FileText size={24} /><p>当前仅展示已获授权并完成解析的内容；摘要之外的全文尚不可用。</p></div>}
          </section>

          {(content?.elements.length || 0) > 0 && <section className="paper-content-card" id="figures"><h2>图表</h2><div className="paper-elements">{content!.elements.map((element) => <button type="button" key={element.id} onClick={() => element.section_id && document.getElementById(`section-${element.section_id}`)?.scrollIntoView({ behavior: "smooth" })}><strong>{element.label || (element.element_type === "figure" ? "图片" : "表格")}</strong><span>{element.caption || "点击定位到正文"}</span></button>)}</div></section>}
        </article>

        <aside className="paper-aside">
          <section className="paper-venue-card">
            <h2>来自期刊</h2>
            <div className="paper-venue-title">{venueCover && isSafeHref(venueCover) && <img src={venueCover} alt={`${work.venue?.title || "期刊"}封面`} referrerPolicy="no-referrer" />}<strong>{work.venue?.title || work.publication.venueTitle || "期刊信息待补充"}</strong></div>
            {work.venue?.publisher && <p>{work.venue.publisher}</p>}
            {venueMetrics.map((metric) => <span key={`${metric.type}-${metric.period || ""}`}>{metricLabel(metric)}</span>)}
            {work.venue?.homepageUrl && isSafeHref(work.venue.homepageUrl) && <a href={work.venue.homepageUrl} target="_blank" rel="noreferrer">期刊主页 <ExternalLink size={13} /></a>}
          </section>
          <section className="paper-relations-card">
            <div className="paper-relation-tabs">
              <button className={relationTab === "references" ? "active" : ""} type="button" onClick={() => setRelationTab("references")}>参考文献 ({relations.references.total})</button>
              <button className={relationTab === "cited_by" ? "active" : ""} type="button" onClick={() => setRelationTab("cited_by")}>施引文献 ({relations.cited_by.total})</button>
            </div>
            <RelationList page={relations[relationTab]} onLoadMore={() => void loadMoreRelations(relationTab)} empty={relationTab === "references" ? "暂未获取参考文献。" : "暂未获取施引文献。"} />
          </section>
        </aside>
      </div>
      <CitationDialog workId={work.id} title={work.title} open={citationOpen} onClose={() => setCitationOpen(false)} />
    </main>
  );
}
